/*
 * SearchBar.jsx
 * -------------
 * Global search overlay that queries across all checklists and items.
 * Results are grouped by type (checklists, items) and show context.
 * Supports keyboard navigation through results.
 * Debounces input to avoid excessive API calls.
 * Rendered inside Nav as a dropdown below the top bar.
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import CategoryTag from "./CategoryTag";

// ============================================
// DEBOUNCE HOOK
// ============================================
function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}

// ============================================
// HIGHLIGHT MATCHING TEXT
// Wraps matched substring in a gold span
// ============================================
function HighlightMatch({ text, query }) {
  if (!query || !text) return <span>{text}</span>;

  const index = text.toLowerCase().indexOf(query.toLowerCase());
  if (index === -1) return <span>{text}</span>;

  return (
    <span>
      {text.slice(0, index)}
      <span style={{
        background: "var(--accent-gold-muted)",
        color: "var(--accent-gold)",
        fontWeight: 700,
        borderRadius: "2px",
        padding: "0 2px"
      }}>
        {text.slice(index, index + query.length)}
      </span>
      {text.slice(index + query.length)}
    </span>
  );
}

// ============================================
// RESULT ROW
// ============================================
function ResultRow({ result, query, isSelected, onClick }) {
  const isChecklist = result.type === "checklist";

  return (
    <div
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--space-3)",
        padding: "var(--space-3) var(--space-4)",
        background: isSelected ? "var(--accent-green-muted)" : "transparent",
        cursor: "pointer",
        borderRadius: "var(--radius-sm)",
        transition: "background var(--transition-fast)"
      }}
      onMouseEnter={e => {
        if (!isSelected) e.currentTarget.style.background = "var(--bg-surface-2)";
      }}
      onMouseLeave={e => {
        if (!isSelected) e.currentTarget.style.background = "transparent";
      }}
    >
      {/* Icon */}
      <div style={{
        width: "32px",
        height: "32px",
        borderRadius: "var(--radius-sm)",
        background: isChecklist ? "var(--accent-green-muted)" : "var(--bg-surface-2)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "16px",
        flexShrink: 0
      }}>
        {isChecklist ? "📋" : "✓"}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          fontFamily: "var(--font-body)",
          fontSize: "14px",
          fontWeight: 500,
          color: "var(--text-primary)",
          margin: "0 0 2px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap"
        }}>
          <HighlightMatch text={result.title} query={query} />
        </p>
        {result.subtitle && (
          <p style={{
            fontFamily: "var(--font-body)",
            fontSize: "12px",
            color: "var(--text-muted)",
            margin: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}>
            <HighlightMatch text={result.subtitle} query={query} />
          </p>
        )}
      </div>

      {/* Category tag */}
      {result.category && (
        <CategoryTag category={result.category} size="sm" showIcon={false} />
      )}

      {/* Keyboard hint */}
      {isSelected && (
        <span style={{
          fontFamily: "var(--font-body)",
          fontSize: "11px",
          color: "var(--text-muted)",
          flexShrink: 0
        }}>
          ↵ Open
        </span>
      )}
    </div>
  );
}

// ============================================
// RESULTS GROUP
// ============================================
function ResultsGroup({ label, results, query, selectedIndex, globalOffset, onSelect }) {
  if (results.length === 0) return null;

  return (
    <div>
      <p style={{
        fontFamily: "var(--font-body)",
        fontSize: "11px",
        fontWeight: 600,
        color: "var(--text-muted)",
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        padding: "var(--space-2) var(--space-4) var(--space-1)",
        margin: 0
      }}>
        {label}
      </p>
      {results.map((result, i) => (
        <ResultRow
          key={result.id}
          result={result}
          query={query}
          isSelected={selectedIndex === globalOffset + i}
          onClick={() => onSelect(result)}
        />
      ))}
    </div>
  );
}

// ============================================
// EMPTY STATE
// ============================================
function EmptyState({ query }) {
  return (
    <div style={{
      padding: "var(--space-7) var(--space-4)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "var(--space-2)"
    }}>
      <span style={{ fontSize: "32px" }}>🔍</span>
      <p style={{
        fontFamily: "var(--font-body)",
        fontSize: "14px",
        fontWeight: 600,
        color: "var(--text-primary)",
        margin: 0
      }}>
        No results for "{query}"
      </p>
      <p style={{
        fontFamily: "var(--font-body)",
        fontSize: "13px",
        color: "var(--text-muted)",
        margin: 0
      }}>
        Try searching for a checklist title or item name
      </p>
    </div>
  );
}

// ============================================
// MAIN EXPORT
// ============================================
export default function SearchBar({ onClose }) {
  const navigate = useNavigate();
  const { user, isGuest } = useAuth();
  const inputRef = useRef(null);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState({ checklists: [], items: [] });
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const debouncedQuery = useDebounce(query, 300);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // ============================================
  // SEARCH -- runs on debounced query change
  // ============================================
  const search = useCallback(async (q) => {
    if (!q || q.trim().length < 2) {
      setResults({ checklists: [], items: [] });
      return;
    }

    setLoading(true);
    try {
      const headers = { "Content-Type": "application/json" };
      if (isGuest) headers["x-guest-id"] = user?.id;

      const res = await fetch("/api/checklists", { headers });
      const checklists = await res.json();

      const lowerQ = q.toLowerCase();

      // Match checklists by title
      const matchedChecklists = checklists
        .filter(c => c.title.toLowerCase().includes(lowerQ))
        .slice(0, 5)
        .map(c => ({
          id: c.id,
          type: "checklist",
          title: c.title,
          subtitle: `${c.items?.length || 0} items`,
          category: c.category,
          navigateTo: `/checklists/${c.id}`
        }));

      // Match items within checklists
      const matchedItems = [];
      for (const checklist of checklists) {
        for (const item of (checklist.items || [])) {
          if (item.text.toLowerCase().includes(lowerQ)) {
            matchedItems.push({
              id: `${checklist.id}-${item.id}`,
              type: "item",
              title: item.text,
              subtitle: checklist.title,
              category: checklist.category,
              navigateTo: `/checklists/${checklist.id}`
            });
          }
          if (matchedItems.length >= 8) break;
        }
        if (matchedItems.length >= 8) break;
      }

      setResults({ checklists: matchedChecklists, items: matchedItems });
      setSelectedIndex(0);
    } catch (err) {
      console.error("Search error:", err);
    } finally {
      setLoading(false);
    }
  }, [user, isGuest]);

  useEffect(() => {
    search(debouncedQuery);
  }, [debouncedQuery, search]);

  // ============================================
  // KEYBOARD NAVIGATION
  // ============================================
  const allResults = [...results.checklists, ...results.items];

  function handleKeyDown(e) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, allResults.length - 1));
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    }
    if (e.key === "Enter" && allResults[selectedIndex]) {
      handleSelect(allResults[selectedIndex]);
    }
    if (e.key === "Escape") {
      onClose();
    }
  }

  function handleSelect(result) {
    navigate(result.navigateTo);
    onClose();
  }

  const hasResults = allResults.length > 0;
  const showEmpty = debouncedQuery.length >= 2 && !loading && !hasResults;

  return (
    <div style={{ width: "100%", maxWidth: "600px", margin: "0 auto" }}>

      {/* Search input */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--space-3)",
        background: "var(--bg-surface-2)",
        border: "1px solid var(--accent-green)",
        borderRadius: "var(--radius-md)",
        padding: "var(--space-3) var(--space-4)",
        boxShadow: "var(--shadow-md)"
      }}>
        {/* Search icon */}
        {loading ? (
          <div style={{
            width: "18px",
            height: "18px",
            border: "2px solid var(--border)",
            borderTopColor: "var(--accent-green)",
            borderRadius: "50%",
            animation: "spin 600ms linear infinite",
            flexShrink: 0
          }} />
        ) : (
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ flexShrink: 0, color: "var(--text-muted)" }}>
            <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M13 13L16 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        )}

        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search checklists and items..."
          style={{
            flex: 1,
            background: "none",
            border: "none",
            outline: "none",
            fontFamily: "var(--font-body)",
            fontSize: "15px",
            color: "var(--text-primary)",
            caretColor: "var(--accent-green)"
          }}
        />

        {/* Clear button */}
        {query && (
          <button
            onClick={() => setQuery("")}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "var(--text-muted)",
              padding: "2px",
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              transition: "color var(--transition-fast)"
            }}
            onMouseEnter={e => e.currentTarget.style.color = "var(--text-primary)"}
            onMouseLeave={e => e.currentTarget.style.color = "var(--text-muted)"}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        )}

        {/* Keyboard shortcut hint */}
        <kbd style={{
          fontFamily: "var(--font-body)",
          fontSize: "11px",
          color: "var(--text-muted)",
          background: "var(--bg-surface)",
          border: "1px solid var(--border)",
          borderRadius: "4px",
          padding: "2px 6px",
          flexShrink: 0
        }}>
          Esc
        </kbd>
      </div>

      {/* Results dropdown */}
      {(hasResults || showEmpty) && (
        <div style={{
          marginTop: "var(--space-2)",
          background: "var(--bg-surface)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-md)",
          boxShadow: "var(--shadow-lg)",
          overflow: "hidden",
          maxHeight: "400px",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch"
        }}>
          {showEmpty ? (
            <EmptyState query={debouncedQuery} />
          ) : (
            <>
              <ResultsGroup
                label="Checklists"
                results={results.checklists}
                query={debouncedQuery}
                selectedIndex={selectedIndex}
                globalOffset={0}
                onSelect={handleSelect}
              />
              {results.checklists.length > 0 && results.items.length > 0 && (
                <div style={{ height: "1px", background: "var(--border)", margin: "var(--space-1) 0" }} />
              )}
              <ResultsGroup
                label="Items"
                results={results.items}
                query={debouncedQuery}
                selectedIndex={selectedIndex}
                globalOffset={results.checklists.length}
                onSelect={handleSelect}
              />
            </>
          )}
        </div>
      )}

      {/* Keyboard hint footer */}
      {hasResults && (
        <div style={{
          display: "flex",
          gap: "var(--space-4)",
          padding: "var(--space-2) var(--space-2) 0",
          justifyContent: "flex-end"
        }}>
          {[
            { key: "↑↓", label: "Navigate" },
            { key: "↵", label: "Open" },
            { key: "Esc", label: "Close" }
          ].map(({ key, label }) => (
            <span key={key} style={{
              fontFamily: "var(--font-body)",
              fontSize: "11px",
              color: "var(--text-muted)",
              display: "flex",
              alignItems: "center",
              gap: "4px"
            }}>
              <kbd style={{
                background: "var(--bg-surface)",
                border: "1px solid var(--border)",
                borderRadius: "4px",
                padding: "1px 5px",
                fontSize: "11px"
              }}>
                {key}
              </kbd>
              {label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}